import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PageContainer, PageSection } from "@/components/layout/PageShell"
import { cn } from "@/lib/utils"

type CTASectionProps = {
  title: string
  description: string
  primaryLabel: string
  primaryHref: string
  secondaryLabel: string
  secondaryHref: string
  sectionClassName?: string
  showPattern?: boolean
  invertPrimary?: boolean
  contrast?: "light" | "dark"
}

export function CTASection({
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  sectionClassName,
  showPattern = false,
  invertPrimary = false,
  contrast = "light",
}: CTASectionProps) {
  const isDark = contrast === "dark"

  return (
    <PageSection
      className={cn(
        "relative overflow-hidden bg-gradient-to-br from-slate-100 via-white to-slate-100",
        sectionClassName
      )}
    >
      {showPattern && (
        <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-[0.06]" />
      )}

      <PageContainer className="relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2
            className={cn(
              "text-3xl md:text-4xl font-bold mb-4",
              isDark ? "text-white" : "text-slate-900"
            )}
          >
            {title}
          </h2>
          <p
            className={cn(
              "text-lg mb-8 max-w-2xl mx-auto",
              isDark ? "text-white/85" : "text-slate-600"
            )}
          >
            {description}
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="xl"
              variant={invertPrimary ? "default" : "cta"}
              className={cn(
                invertPrimary &&
                  (isDark
                    ? "bg-white text-slate-900 hover:bg-white/90"
                    : "bg-slate-900 text-white hover:bg-slate-800")
              )}
              asChild
            >
              <a href={primaryHref} target="_blank" rel="noopener noreferrer">
                {primaryLabel}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>

            <Button
              size="xl"
              variant="outline"
              className={cn(
                isDark
                  ? "border-white/50 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                  : "border-slate-300 bg-slate-100/70 text-slate-700 hover:border-slate-400 hover:bg-slate-200 hover:text-slate-900"
              )}
              asChild
            >
              <a href={secondaryHref}>{secondaryLabel}</a>
            </Button>
          </div>
        </motion.div>
      </PageContainer>
    </PageSection>
  )
}
